import React, { useState } from 'react'

import { Button } from '@octane/spark'
import { FormValues } from 'containers/FormBuilder'

import {
    DevQuickFillContainerStyle,
    DevQuickFillTextarea,
} from './DeveloperFormDataQuickFill.styled'

const DEVELOPER_APPLICATION_DATA_OVERRIDE_SETTING =
    'enableDeveloperApplicationDataOverride'

function processJsonString(str: string): string {
    /**
     * When applicant JSON is generated programmatically it will be
     * enclosed within single double quotes AND will have all other
     * double quotes duplicated (i.e. """" vs "").
     *
     * This function will clean up the strange formatting mentioned above but
     * leave other strings unchanged.
     */
    let outputString = str

    if (outputString.startsWith('"')) {
        // remove leading double quote
        outputString = outputString.substring(1)

        if (outputString.endsWith('"')) {
            // remove trailing outer double quote
            outputString = outputString.substring(0, outputString.length - 1)
        }

        // replace duplicate double quotes with single double quote
        outputString = outputString.replace(/""/g, '"')
    }

    return outputString
}

function jsonArrayToObjectsArray(stringsArray: string[]): FormValues[] {
    return stringsArray
        .map((str: string) => str.trim())
        .map(processJsonString)
        .map<FormValues>((cleanedString: string) => {
            return cleanedString ? JSON.parse(cleanedString) : {}
        })
}

function mergeObjects(objectsArray: FormValues[]): FormValues {
    return objectsArray.reduce<FormValues>(
        (prevData: FormValues, jsonData: FormValues) => {
            return { ...prevData, ...jsonData }
        },
        {},
    )
}

function applyOverride(
    jsonDataArray: string[],
    setDeveloperDataOverride: (data: FormValues) => void,
) {
    const dataObjectsArray = jsonArrayToObjectsArray(jsonDataArray)
    const overrideData = mergeObjects(dataObjectsArray)

    setDeveloperDataOverride({
        __developerDataOverrideTimestamp__: Date.now(), // force updates on re-renders by updating a timestamp value
        ...overrideData,
    })
}

function createTextArea(
    index: number,
    developerApplicationData: string[],
    setDeveloperApplicationData: (state: string[]) => void,
) {
    return (
        <DevQuickFillTextarea
            key={index}
            onChange={(e) => {
                const newData = [...developerApplicationData]

                newData[index] = e.target.value
                setDeveloperApplicationData(newData)
            }}
        />
    )
}

function shouldRender() {
    const settingEnabled =
        window.localStorage.getItem(
            DEVELOPER_APPLICATION_DATA_OVERRIDE_SETTING,
        ) === 'true'

    const isProduction = window.config?.ENVIRONMENT === 'production'

    return settingEnabled && !isProduction
}

interface DeveloperFormDataQuickFillProps {
    setDeveloperOverrideData: (applicationData: FormValues) => void
}

export default function DeveloperFormDataQuickFill({
    setDeveloperOverrideData,
}: DeveloperFormDataQuickFillProps) {
    const [developerApplicationData, setDeveloperApplicationData] = useState([
        '',
        '',
    ])

    if (!shouldRender()) {
        return null
    }

    return (
        <DevQuickFillContainerStyle data-testid="developer-form-data-quick-fill">
            <h2>Developer Tool — Quick Fill</h2>
            <p>
                Paste your test applicant or vehicle snippets here. <br />
                Higher boxes will be overwritten by lower boxes.
            </p>
            {developerApplicationData.map((_textState, index) => {
                return createTextArea(
                    index,
                    developerApplicationData,
                    setDeveloperApplicationData,
                )
            })}
            <Button
                type="button"
                onClick={() =>
                    applyOverride(
                        developerApplicationData,
                        setDeveloperOverrideData,
                    )
                }
                fullWidth={false}
                size="small"
            >
                Apply Override
            </Button>
        </DevQuickFillContainerStyle>
    )
}

export const testExports = {
    DEVELOPER_APPLICATION_DATA_OVERRIDE_SETTING,
    processJsonString,
    jsonArrayToObjectsArray,
    mergeObjects,
    applyOverride,
}
